import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { ToastrService } from "ngx-toastr";
import { Component, inject } from "@angular/core";
import { DevPowerBIService } from "@app/admin/services/devpowerbi.service";
import { DevPowerBIMaintenanceModel, DevPowerBIModel } from "@app/admin/shared/dev-powerbi-model";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { finalize, firstValueFrom, of } from "rxjs";
import { SharedModule } from "@app/shared/shared.module";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/admin/i18n/scope";

@Component({
    selector: "dev-powerbi",
    templateUrl: "./dev-powerbi.component.html",
    styleUrls: ["./dev-powerbi.component.scss"],
    imports: [SharedModule, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class DevPowerBIComponent {
    private _ds = inject(DevPowerBIService);
    private _prompt = inject(CerrixPromptService);
    private _toastr = inject(ToastrService);
    private translation = injectTranslation(scope);

    config: GenericListConfig;
    isEnabled = false;
    isLoading = true;

    constructor() {
        const _ds = this._ds;

        _ds.isEnabled().then((e) => {
            this.isEnabled = e;
            if (this.isEnabled) {
                this.init();
            } else {
                this.isLoading = false;
            }
        });
    }

    async init() {
        const data = await this._ds.getReports();
        await this.translation.ready();
        this.createListConfig(data);
        this.isLoading = false;
    }

    async createTemplateDatasets() {
        await this.translation.ready();
        const loaderPrompt = this._prompt.loader(this.translation.t("devPowerBi.creatingTemplates"));
        firstValueFrom(
            this._ds.createTemplates().pipe(
                finalize(() => {
                    loaderPrompt.close();
                })
            )
        ).then(() => {
            this._toastr.success("", this.translation.t("devPowerBi.templatesCreated"));
        });
    }

    private createListConfig(data: DevPowerBIMaintenanceModel) {
        this.config = <GenericListConfig>{
            name: this.translation.t("devPowerBi.listName"),
            data: data.reports,
            idProp: "id",

            allowAdd: true,
            allowEdit: true,
            allowDelete: true,

            fields: [
                {
                    fieldName: "nameInPowerBI",
                    prettyName: this.translation.t("devPowerBi.nameInPowerBiField"),
                    required: true,
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "name",
                    prettyName: this.translation.t("devPowerBi.nameField"),
                    required: true,
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "description",
                    prettyName: this.translation.t("devPowerBi.descriptionField"),
                    required: true,
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    fieldName: "module",
                    prettyName: this.translation.t("devPowerBi.moduleField"),
                    required: true,
                    fieldType: GenericListFieldType.SingleSelect,
                    getDataMethod: () => {
                        return of(
                            Object.keys(data.modules).map(
                                (x) =>
                                    <IdNameCombination>{
                                        ID: +x,
                                        Name: data.modules[x],
                                    }
                            )
                        );
                    },
                },
                {
                    fieldName: "dataset",
                    prettyName: this.translation.t("devPowerBi.datasetField"),
                    required: false,
                    fieldType: GenericListFieldType.SingleSelect,
                    getDataMethod: () => {
                        return of(
                            Object.keys(data.datasets).map(
                                (x) =>
                                    <IdNameCombination>{
                                        ID: +x,
                                        Name: data.datasets[x],
                                    }
                            )
                        );
                    },
                },
                {
                    fieldName: "requiredRoleGroup",
                    prettyName: this.translation.t("devPowerBi.requiredRoleGroupField"),
                    required: false,
                    fieldType: GenericListFieldType.SingleSelect,
                    getDataMethod: () => {
                        return of(
                            Object.keys(data.roleGroups).map(
                                (x) =>
                                    <IdNameCombination>{
                                        ID: +x,
                                        Name: data.roleGroups[x],
                                    }
                            )
                        );
                    },
                },
            ],

            add: async (row: DevPowerBIModel) => {
                const id = await this._ds.updateReport(row);
                row.id = id;
                this._toastr.success(
                    this.translation.t("devPowerBi.reportRegistered"),
                    this.translation.t("devPowerBi.success")
                );
                return true;
            },

            edit: async (row: DevPowerBIModel) => {
                await this._ds.updateReport(row);
                this._toastr.success(
                    this.translation.t("devPowerBi.reportUpdated"),
                    this.translation.t("devPowerBi.success")
                );
                return true;
            },

            delete: async (row: DevPowerBIModel) => {
                const confirmed = await this._prompt
                    .confirm(
                        this.translation.t("devPowerBi.deleteReportTitle", { name: row.name }),
                        this.translation.t("devPowerBi.deleteReportMessage")
                    )
                    .toPromise();

                if (!confirmed) return false;

                await this._ds.deleteReport(row.id);
                this._toastr.success(
                    this.translation.t("devPowerBi.reportDeleted"),
                    this.translation.t("devPowerBi.success")
                );
                return true;
            },
        };
    }
}
